import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { IconArrowHead, Modal, ResponsiveImage } from '../../../components';

import css from './PhotoLightbox.module.css';

/**
 * The photos of a listing, over the whole page.
 *
 * It opens from a thumbnail and is left by clicking away from the photo, by pressing escape,
 * or by swiping past the last one. There is no close button: the way out is to click the
 * page you came from, which is most of what is on screen.
 *
 * Built on the marketplace Modal, which already handles the portal, escape, locking the page
 * behind it, and putting focus back where it was. Its close button is the one piece that does
 * not belong, and the stylesheet hides it.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.image>} props.images - The photos to show
 * @param {Array<string>} props.imageVariants - Variants to hand to ResponsiveImage
 * @param {number} props.startIndex - Which photo to open on
 * @param {boolean} props.isOpen - Whether the lightbox is showing
 * @param {Function} props.onClose - Called when it should close
 * @param {Function} props.onManageDisableScrolling - Locks the page behind the lightbox
 * @returns {JSX.Element} the lightbox
 */

// How far a finger has to travel before it counts as a swipe rather than a tap. Below this,
// a touch is a click on the backdrop, which closes.
const SWIPE_THRESHOLD_IN_PX = 45;

const PhotoLightbox = props => {
  const {
    images = [],
    imageVariants,
    startIndex = 0,
    isOpen,
    onClose,
    onManageDisableScrolling,
  } = props;
  const intl = useIntl();
  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef(null);

  // Opening from a different thumbnail has to move the lightbox, and it stays mounted
  // between openings, so the index cannot simply be the initial state.
  useEffect(() => {
    if (isOpen) {
      setIndex(startIndex);
    }
  }, [isOpen, startIndex]);

  const count = images.length;
  // Wrapping around means the arrows are never dead ends, and a swipe past the last photo
  // carries on rather than stopping.
  const step = by => setIndex(current => (current + by + count) % count);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = e => {
      if (e.key === 'ArrowLeft') {
        step(-1);
      } else if (e.key === 'ArrowRight') {
        step(1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, count]);

  if (count === 0) {
    return null;
  }

  const onTouchStart = e => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = e => {
    const from = touchStartX.current;
    touchStartX.current = null;
    if (from == null) {
      return;
    }
    const travelled = (e.changedTouches[0]?.clientX ?? from) - from;
    if (Math.abs(travelled) >= SWIPE_THRESHOLD_IN_PX) {
      step(travelled < 0 ? 1 : -1);
    }
  };

  // Everything in here sits on the backdrop, so anything that is not a way of moving between
  // photos has to keep its click to itself or it would close the lightbox.
  const keepOpen = handler => e => {
    e.stopPropagation();
    if (handler) {
      handler();
    }
  };

  const arrow = (direction, label) => (
    <button
      className={classNames(css.nav, direction === 'left' ? css.navLeft : css.navRight)}
      onClick={keepOpen(() => step(direction === 'left' ? -1 : 1))}
      title={intl.formatMessage({ id: label })}
      aria-label={intl.formatMessage({ id: label })}
    >
      <IconArrowHead direction={direction} size="big" rootClassName={css.navIcon} />
    </button>
  );

  return (
    <Modal
      id="PhotoLightbox"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      className={css.modal}
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.container}
      contentClassName={css.content}
    >
      {isOpen ? (
        <div
          className={css.stage}
          onClick={onClose}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {count > 1 ? arrow('left', 'PhotoLightbox.previous') : null}

          <div className={css.frame} onClick={keepOpen()}>
            <ResponsiveImage
              rootClassName={css.photo}
              image={images[index]}
              variants={imageVariants}
              alt={intl.formatMessage(
                { id: 'PhotoLightbox.imageAltText' },
                { index: index + 1, count }
              )}
              sizes="100vw"
            />
          </div>

          {count > 1 ? arrow('right', 'PhotoLightbox.next') : null}

          {count > 1 ? (
            <p className={css.counter} onClick={keepOpen()}>
              <FormattedMessage id="PhotoLightbox.counter" values={{ index: index + 1, count }} />
            </p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
};

export default PhotoLightbox;
